/**
 * 🚀 CTAs PRINCIPAUX - EXPÉRIENCE SPECTACULAIRE !
 * 
 * ✨ Features:
 * - 4 sons spatiaux uniques pour chaque bouton
 * - Effet K2000 (lumière qui défile)
 * - Badges "GRATUIT" et "OFFRE" bien visibles
 * - Effets de lumière géniaux et inoubliables !
 * - 🎴 Cartes de fidélité flottantes "Votre fidélité récompensée !"
 */

import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Gift, User, Truck, Phone, ChevronRight, Star, Sparkles } from 'lucide-react';

// 🎴 CARTES DE FIDÉLITÉ FLOTTANTES - "Votre fidélité récompensée !"
const FloatingLoyaltyCards = () => {
  // Les 3 cartes de fidélité avec leurs styles
  const cards = [
    {
      id: 1,
      gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
      title: 'CARTE VIP',
      subtitle: 'Client fidèle',
      icon: '⭐',
      stamps: 7,
      maxStamps: 10,
      rotation: -15,
      delay: 0,
      x: -20,
      y: 0,
      z: 1
    },
    {
      id: 2,
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
      title: 'CARTE VIP',
      subtitle: 'Récompenses',
      icon: '🎁',
      stamps: 4,
      maxStamps: 10,
      rotation: 5,
      delay: 0.1,
      x: 0,
      y: -10,
      z: 3
    },
    {
      id: 3,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
      title: 'POINTS BONUS',
      subtitle: '250',
      icon: '💎',
      stamps: 9,
      maxStamps: 10,
      rotation: 18,
      delay: 0.2,
      x: 20,
      y: 5,
      z: 2
    }
  ];

  return (
    <motion.div 
      className="relative mb-10"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      {/* Titre avec étoile */}
      <motion.div 
        className="text-center mb-6"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <motion.div 
          className="inline-flex items-center gap-2 px-6 py-2 rounded-full"
          style={{
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.2) 100%)',
            border: '1px solid rgba(251, 191, 36, 0.4)',
            boxShadow: '0 0 30px rgba(251, 191, 36, 0.3)'
          }}
          animate={{
            boxShadow: [
              '0 0 20px rgba(251, 191, 36, 0.2)',
              '0 0 40px rgba(251, 191, 36, 0.4)',
              '0 0 20px rgba(251, 191, 36, 0.2)'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
          </motion.div>
          <span className="text-lg font-bold text-yellow-300">Votre fidélité récompensée !</span>
          <motion.div
            animate={{ rotate: [0, -15, 15, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          >
            <Sparkles className="w-5 h-5 text-yellow-400" />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Les 3 cartes flottantes */}
      <div className="relative h-44 flex justify-center items-center">
        {cards.map((card, index) => (
          <motion.div
            key={card.id}
            className="absolute w-32 sm:w-40 cursor-pointer"
            style={{
              zIndex: card.z,
              transform: `translateX(${card.x}px)`
            }}
            initial={{ 
              opacity: 0, 
              y: 50, 
              rotate: card.rotation,
              x: card.x * 2
            }}
            whileInView={{ 
              opacity: 1, 
              y: card.y, 
              rotate: card.rotation,
              x: card.x
            }}
            viewport={{ once: true }}
            transition={{ 
              delay: card.delay + 0.3,
              duration: 0.6,
              type: 'spring',
              stiffness: 100
            }}
            animate={{
              y: [card.y, card.y - 8, card.y],
              rotate: [card.rotation, card.rotation + 2, card.rotation - 2, card.rotation]
            }}
            whileHover={{ 
              scale: 1.15, 
              zIndex: 10,
              rotate: 0,
              y: -20,
              transition: { duration: 0.3 }
            }}
          >
            {/* La carte elle-même */}
            <div 
              className="relative rounded-xl p-3 overflow-hidden"
              style={{
                background: card.gradient,
                boxShadow: '0 10px 40px rgba(0,0,0,0.4), 0 0 20px rgba(139, 92, 246, 0.3)',
                aspectRatio: '1.6/1'
              }}
            >
              {/* Effet brillant */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
              />
              
              {/* Motif holographique */}
              <div 
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.1) 5px, rgba(255,255,255,0.1) 10px)'
                }}
              />
              
              {/* Contenu de la carte */}
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex justify-between items-start">
                  <span className="text-2xl">{card.icon}</span>
                  <div className="text-right">
                    <div className="text-[10px] font-bold text-white/90 tracking-wider">{card.title}</div>
                    <div className="text-xs text-white/70">{card.subtitle}</div>
                  </div>
                </div>
                
                {/* Barre de progression des tampons */}
                <div className="mt-2">
                  <div className="flex gap-0.5 justify-center">
                    {[...Array(card.maxStamps)].map((_, i) => (
                      <div 
                        key={i}
                        className={`w-2 h-2 rounded-full ${
                          i < card.stamps 
                            ? 'bg-white shadow-[0_0_6px_rgba(255,255,255,0.8)]' 
                            : 'bg-white/30'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="text-center text-[9px] text-white/80 mt-1">
                    {card.stamps}/{card.maxStamps} tampons
                  </div>
                </div>
              </div>
              
              {/* Bord lumineux */}
              <div className="absolute inset-0 rounded-xl border border-white/20" />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Particules scintillantes autour des cartes */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full bg-yellow-300"
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${20 + Math.random() * 60}%`,
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
            y: [0, -20, -40]
          }}
          transition={{
            duration: 2 + Math.random(),
            repeat: Infinity,
            delay: i * 0.3
          }}
        />
      ))}
    </motion.div>
  );
};

// 🔊 SONS SPATIAUX - Un son unique pour chaque bouton
const SPACE_SOUNDS = {
  livraison: () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    // Son de décollage spatial - whoooosh montant
    osc.frequency.setValueAtTime(150, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.3);
    osc.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.5);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  },
  
  espaceVIP: () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'triangle';
    // Son de portail magique - bing bing bing
    osc.frequency.setValueAtTime(523, ctx.currentTime);
    osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
    osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
    osc.frequency.setValueAtTime(1047, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  },
  
  telephone: () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc2.type = 'sine';
    // Son de communication spatiale - beep beep
    osc.frequency.setValueAtTime(440, ctx.currentTime);
    osc2.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.setValueAtTime(0, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.2, ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc2.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
    osc2.stop(ctx.currentTime + 0.3);
  },
  
  offre: () => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'square';
    // Son de jackpot spatial - ta-da !
    osc.frequency.setValueAtTime(392, ctx.currentTime);
    osc.frequency.setValueAtTime(523, ctx.currentTime + 0.1);
    osc.frequency.setValueAtTime(659, ctx.currentTime + 0.2);
    osc.frequency.setValueAtTime(784, ctx.currentTime + 0.25);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.setValueAtTime(0.2, ctx.currentTime + 0.25);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  }
};

const ActionButton = ({ 
  onClick, 
  href,
  gradient, 
  glowColor,
  k2000Color,
  icon: Icon, 
  title, 
  subtitle,
  badge,
  badgeColor,
  delay = 0,
  testId,
  soundKey
}) => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  
  const playSound = useCallback(() => {
    try {
      if (soundKey && SPACE_SOUNDS[soundKey]) {
        SPACE_SOUNDS[soundKey]();
      }
    } catch (e) {
      // Silent fail
    }
  }, [soundKey]);
  
  const handleClick = () => {
    playSound();
    
    if (href) {
      if (href.startsWith('tel:')) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    }
    onClick?.();
  };

  return (
    <motion.button
      ref={buttonRef}
      onClick={handleClick}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
      className="relative w-full group"
      data-testid={testId}
    >
      {/* Glow effect animé */}
      <motion.div
        className="absolute -inset-2 rounded-3xl opacity-50 blur-xl"
        style={{ background: gradient }}
        animate={{
          opacity: [0.3, 0.6, 0.3],
          scale: [1, 1.02, 1]
        }}
        transition={{ duration: 3, repeat: Infinity }}
      />
      
      <div 
        className="relative overflow-hidden rounded-2xl p-5 transition-all duration-300 group-hover:scale-[1.02]"
        style={{
          background: gradient,
          boxShadow: `0 0 40px ${glowColor}, inset 0 1px 0 rgba(255,255,255,0.2)`
        }}
      >
        {/* ✨ EFFET K2000 - Lumière qui défile ! */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `linear-gradient(90deg, transparent 0%, ${k2000Color} 50%, transparent 100%)`,
            opacity: 0.4
          }}
          animate={{
            x: ['-100%', '200%']
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'linear',
            repeatDelay: 1
          }}
        />
        
        {/* Shine effect au hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
        
        {/* ✨ Particules flottantes */}
        <motion.div
          className="absolute top-2 right-16 w-1 h-1 rounded-full bg-white/60"
          animate={{ y: [0, -10, 0], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2, repeat: Infinity, delay: 0 }}
        />
        <motion.div
          className="absolute top-4 right-24 w-1.5 h-1.5 rounded-full bg-white/40"
          animate={{ y: [0, -8, 0], opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
        />
        <motion.div
          className="absolute bottom-3 right-20 w-1 h-1 rounded-full bg-white/50"
          animate={{ y: [0, -6, 0], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 1.8, repeat: Infinity, delay: 0.3 }}
        />
        
        {/* 🏷️ Badge bien visible - corrigé ! */}
        {badge && (
          <motion.div
            className="absolute top-3 right-3 px-3 py-1.5 rounded-lg text-xs font-black z-20 uppercase tracking-wide"
            style={{
              background: badgeColor || 'linear-gradient(135deg, #fbbf24, #f59e0b)',
              color: '#000',
              boxShadow: '0 0 20px rgba(251, 191, 36, 0.6), 0 2px 10px rgba(0,0,0,0.3)'
            }}
            animate={{
              scale: [1, 1.05, 1],
              boxShadow: [
                '0 0 15px rgba(251, 191, 36, 0.6)',
                '0 0 25px rgba(251, 191, 36, 0.9)',
                '0 0 15px rgba(251, 191, 36, 0.6)'
              ]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            {badge}
          </motion.div>
        )}

        <div className="relative flex items-center gap-4">
          {/* Icon avec glow */}
          <motion.div
            className="w-14 h-14 rounded-xl flex items-center justify-center backdrop-blur-sm relative"
            style={{
              background: 'rgba(255,255,255,0.15)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)'
            }}
            whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
            transition={{ duration: 0.5 }}
          >
            <Icon className="w-7 h-7 text-white drop-shadow-lg" />
            
            {/* Pulse autour de l'icône */}
            <motion.div
              className="absolute inset-0 rounded-xl"
              style={{ border: '2px solid rgba(255,255,255,0.3)' }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0, 0.5]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>

          {/* Text */}
          <div className="flex-1 text-left">
            <h3 className="text-lg md:text-xl font-bold text-white drop-shadow-md">{title}</h3>
            {subtitle && <p className="text-white/90 text-sm drop-shadow">{subtitle}</p>}
          </div>

          {/* Arrow animé */}
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronRight className="w-7 h-7 text-white/80 group-hover:text-white transition-colors" />
          </motion.div>
        </div>
        
        {/* Bottom light bar - effet néon */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1"
          style={{
            background: `linear-gradient(90deg, transparent, ${k2000Color}, transparent)`
          }}
          animate={{
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>
    </motion.button>
  );
};

const MainCTAsSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative py-16 px-4">
      <div className="max-w-2xl mx-auto">
        {/* 🎴 CARTES DE FIDÉLITÉ FLOTTANTES - "Votre fidélité récompensée !" */}
        <FloatingLoyaltyCards />
        
        {/* CTAs Principaux */}
        <div className="space-y-5">
          {/* 1. Livraison - VERT avec son de décollage */}
          <ActionButton
            href="/livraison"
            gradient="linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)"
            glowColor="rgba(34, 197, 94, 0.5)"
            k2000Color="rgba(255, 255, 255, 0.6)"
            icon={Truck}
            title="Me faire livrer GRATUITEMENT"
            subtitle="🌙 Ramadan: 21h-4h du matin"
            badge="GRATUIT"
            badgeColor="linear-gradient(135deg, #fbbf24, #f59e0b)"
            delay={0}
            testId="cta-livraison"
            soundKey="livraison"
          />

          {/* 2. Espace Client - CYAN avec son de portail */}
          <ActionButton
            href="/client"
            gradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)"
            glowColor="rgba(6, 182, 212, 0.5)"
            k2000Color="rgba(255, 255, 255, 0.5)"
            icon={User}
            title="Mon Espace VIP"
            subtitle="Déjà client ? Accède à ton cockpit"
            delay={0.1}
            testId="cta-espace-client"
            soundKey="espaceVIP"
          />

          {/* 3. Téléphone - VIOLET avec son de communication */}
          <ActionButton
            href="tel:0780648976"
            gradient="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)"
            glowColor="rgba(139, 92, 246, 0.5)"
            k2000Color="rgba(255, 255, 255, 0.5)"
            icon={Phone}
            title="Appelez-nous"
            subtitle="07 80 64 89 76"
            delay={0.2}
            testId="cta-telephone"
            soundKey="telephone"
          />

          {/* 4. Offre -10% - JAUNE/ORANGE avec son de jackpot */}
          <ActionButton
            href="/offre-bienvenue"
            gradient="linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)"
            glowColor="rgba(251, 191, 36, 0.5)"
            k2000Color="rgba(255, 255, 255, 0.6)"
            icon={Gift}
            title="1ère fois ici ? -10% pour toi !"
            subtitle="Offre de bienvenue exclusive"
            badge="OFFRE"
            badgeColor="linear-gradient(135deg, #ec4899, #db2777)"
            delay={0.3}
            testId="cta-offre-bienvenue"
            soundKey="offre"
          />
        </div>
      </div>

      {/* ✨ Decorative floating orbs */}
      <motion.div
        className="absolute left-0 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.2) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }}
        animate={{ x: [0, 30, 0], y: [0, -20, 0], opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute right-0 top-1/3 w-48 h-48 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
          filter: 'blur(40px)'
        }}
        animate={{ x: [0, -25, 0], y: [0, 15, 0], opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 10, repeat: Infinity, delay: 2 }}
      />
      <motion.div
        className="absolute left-1/4 bottom-0 w-32 h-32 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
          filter: 'blur(30px)'
        }}
        animate={{ y: [0, -20, 0], opacity: [0.2, 0.5, 0.2] }}
        transition={{ duration: 6, repeat: Infinity, delay: 1 }}
      />
    </section>
  );
};

export default MainCTAsSection;
